import { useFeatures } from '@/contexts/root-store-context'
import { useObservableValue } from '@/hooks/rx-hooks'

type LightDarkResult<
  T,
  L extends T | null | undefined,
  D extends T | null | undefined,
> = L extends T ? L | NonNullable<D> : D extends T ? NonNullable<L> | D : L | D

/**
 * Returns a function that can be called with light and dark variants of a value,
 * the returned variant will be based on the current color scheme.
 *
 * If the current scheme's variant is falsy, the other will be used as a fallback.
 */
export function useLightDarkFn() {
  const { colorScheme$ } = useFeatures(({ shell }) => ({
    colorScheme$: shell.colorScheme$,
  }))

  const scheme = useObservableValue(colorScheme$)
  return function useLightDarkImpl<
    T,
    L extends T | null | undefined,
    D extends T | null | undefined,
  >(light: L, dark: D): LightDarkResult<T, L, D> {
    switch (scheme) {
      case 'dark':
        return (dark || light) as LightDarkResult<T, L, D>
      default:
        return (light || dark) as LightDarkResult<T, L, D>
    }
  }
}
